import type { JSX } from 'react';

/**
 * Ayuda del portal: cada etiqueta citada abajo es literal, copiada de la
 * pantalla que la usa (no una paráfrasis), para que un usuario pueda
 * buscar el botón exacto que está viendo.
 */
export function HelpPage(): JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-lg font-semibold text-slate-900">Ayuda</h1>

      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-slate-900">
          ¿Cómo me muevo por el portal desde el celular?
        </h2>
        <p className="text-sm text-slate-700">
          Abajo de la pantalla vas a ver una barra fija con 4 pestañas: <strong>"Inicio"</strong>{' '}
          (tu próximo pedido), <strong>"Pedidos"</strong> (el historial completo),{' '}
          <strong>"Remitos"</strong> (tus entregas) y <strong>"Ayuda"</strong> (esta pantalla).
          Tocá cualquiera para moverte, sin necesidad de volver a "Inicio" primero. "Plantillas"
          no tiene su propia pestaña: se accede desde los accesos rápidos de "Inicio" o desde
          "Pedidos".
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-slate-900">
          ¿Qué muestra la pantalla de Inicio?
        </h2>
        <p className="text-sm text-slate-700">
          "Inicio" muestra tu próximo pedido (el de mañana) con la cuenta regresiva hasta la hora
          de corte, y debajo los accesos rápidos <strong>"Cargar pedido"</strong> (o <strong>"Cargar pedido para otra fecha"</strong> si ya cargaste el de mañana) y, si
          tenés permiso, <strong>"Plantillas"</strong>.
          Más abajo vas a ver tus próximos pedidos programados y tus últimas entregas, con enlaces
          "Ver todos los pedidos" y "Ver todos los remitos" para ir al listado completo.
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-slate-900">¿Cómo cargo un pedido?</h2>
        <p className="text-sm text-slate-700">
          Desde el inicio del portal, tocá <strong>"Cargar pedido"</strong>. Si querés partir de tu
          último pedido para esa sede, tocá <strong>"Repetir pedido anterior"</strong> dentro de la
          pantalla de carga. Elegí la sede, la fecha de servicio y ajustá las cantidades por menú con los
          botones <strong>"−"</strong> y <strong>"+"</strong> junto a cada plato; si necesitás
          aclarar algo puntual de un menú, tocá <strong>"Agregar observación"</strong>. Antes de
          enviarlo vas a ver un resumen con las cantidades cargadas — tocá{' '}
          <strong>"Confirmar pedido"</strong> para crearlo, o "Volver" para seguir editando.
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-slate-900">
          ¿Cómo funciona "Repetir pedido anterior"?
        </h2>
        <p className="text-sm text-slate-700">
          Precarga las cantidades de tu último pedido para esa sede, pero solo en las posiciones
          del menú que tienen un plato publicado en la fecha elegida — si el menú del día todavía
          se está cargando, el botón espera a que termine en vez de descartar líneas de más. Una
          línea de tu pedido anterior cuya posición no tiene plato publicado hoy queda afuera, y un
          aviso te informa cuántas se descartaron por esa razón.
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-slate-900">¿Qué es la hora de corte?</h2>
        <p className="text-sm text-slate-700">
          Es el momento límite hasta el cual podés cargar o editar un pedido para una fecha de
          servicio. El portal muestra una cuenta regresiva hacia ese horario en el pedido del
          próximo día; cuando se cumple, el mensaje cambia a <strong>"Corte alcanzado"</strong> y ya
          no se puede editar el pedido directamente.
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-slate-900">
          ¿Qué pasa justo después del corte, antes de que confirmen mi pedido?
        </h2>
        <p className="text-sm text-slate-700">
          Apenas pasa la hora de corte, tu pedido queda «en confirmación»: todavía no está{' '}
          <strong>Confirmado</strong>, pero ya no lo podés editar. En ese estado vas a ver un botón{' '}
          <strong>"Ver pedido"</strong> para consultarlo. Cuando quede confirmado, la pantalla
          muestra en su lugar <strong>"Solicitar cambio"</strong> si necesitás modificarlo.
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-slate-900">
          ¿Cómo pido un cambio después del corte?
        </h2>
        <p className="text-sm text-slate-700">
          Una vez que un pedido está <strong>Confirmado</strong>, ya no se puede editar
          directamente. En su lugar, entrá al detalle del pedido y tocá{' '}
          <strong>"Solicitar cambio"</strong>: un usuario de operaciones va a revisar tu pedido y
          aprobar o rechazar el cambio. Podés cambiar cantidades y observaciones, o ponerle 0 a un
          plato para quitarlo (tiene que quedar al menos uno). Vas a poder ver el estado de tu
          solicitud en la pestaña "Cambios" del detalle del pedido.
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-slate-900">
          ¿Cómo veo mis pedidos anteriores?
        </h2>
        <p className="text-sm text-slate-700">
          Desde la pestaña <strong>"Pedidos"</strong>, en <strong>"Historial de pedidos"</strong>{' '}
          podés filtrar por sede, estado y por rango de fechas con los campos{' '}
          <strong>"Desde"</strong> y <strong>"Hasta"</strong> (formato dd/mm/aaaa). Solo se
          muestran los pedidos de las sedes a las que tenés acceso.
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-slate-900">
          ¿Qué hago si me olvidé la contraseña?
        </h2>
        <p className="text-sm text-slate-700">
          En la pantalla de inicio tocá <strong>"¿Olvidaste tu contraseña?"</strong>, escribí tu
          correo y envialo. Siempre vas a ver el mismo aviso, exista o no la cuenta. Si existe, te
          llega un correo con un enlace: abrilo y elegí una contraseña nueva de al menos 10
          caracteres. El enlace vale por tiempo limitado; si venció, pedí uno nuevo. Si el correo
          no llega, revisá la carpeta de spam o consultá con la empresa, porque el envío depende de
          que el correo saliente esté configurado.
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-slate-900">¿Cómo veo mis remitos?</h2>
        <p className="text-sm text-slate-700">
          Desde la pestaña <strong>"Remitos"</strong> podés filtrar por fecha de entrega, sede y
          estado, y tocar <strong>"Descargar PDF"</strong> para guardar el remito de cualquier
          entrega. Solo se muestran los remitos de las sedes a las que tenés acceso, sin precios.
          Cuando un remito ya está firmado, aparece la leyenda <strong>"Certificado"</strong> con
          quién lo recibió y cuándo, y podés tocar <strong>"Descargar remito firmado"</strong> para
          ver el comprobante con la firma — ya sea un escaneo cargado por reparto o una firma hecha
          en pantalla, la leyenda y la descarga funcionan igual.
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-slate-900">¿Qué son las plantillas?</h2>
        <p className="text-sm text-slate-700">
          Una plantilla carga un pedido recurrente automáticamente, sin que tengas que repetirlo a
          mano cada vez. Se accede desde <strong>"Plantillas"</strong> en "Inicio" o "Pedidos", no
          desde la barra inferior. Solo la ven y la administran quienes pueden crear o editar
          pedidos (Encargado de Compras o Supervisor de Cliente); si tu rol es Consulta de Cliente,
          no vas a ver "Plantillas".
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-slate-900">
          ¿Por qué veo "Sin acceso activo" al entrar?
        </h2>
        <p className="text-sm text-slate-700">
          Significa que tu usuario todavía no tiene un acceso al portal vigente, o que venció.
          Contactá a tu proveedor para que revise tu acceso. Si en cambio ves un mensaje que dice
          que tu cuenta es del back-office, usá la dirección interna del sistema, no el portal.
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-slate-900">¿El portal muestra precios?</h2>
        <p className="text-sm text-slate-700">
          No. El portal muestra cantidades por menú, pero nunca precios unitarios ni totales. Los
          precios de tu contrato los administra tu proveedor.
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-slate-900">
          ¿En qué formato veo las fechas?
        </h2>
        <p className="text-sm text-slate-700">
          Siempre en formato día/mes/año (dd/mm/aaaa), tanto en el historial de pedidos como en los
          remitos y en la fecha de servicio de cada pedido.
        </p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-slate-900">¿Qué significa cada estado?</h2>
        <dl className="flex flex-col gap-2 text-sm text-slate-700">
          <div>
            <dt className="font-medium text-slate-900">Borrador</dt>
            <dd>Todavía se puede editar libremente; no fue enviado.</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-900">Enviado</dt>
            <dd>Se envió para su procesamiento, pero todavía no fue confirmado.</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-900">Confirmado</dt>
            <dd>
              El pedido quedó definitivo. Para cambiarlo después de esta etapa hay que usar{' '}
              <strong>"Solicitar cambio"</strong>.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-slate-900">Cancelado</dt>
            <dd>El pedido fue cancelado y no se va a preparar.</dd>
          </div>
        </dl>

        <dl className="flex flex-col gap-2 text-sm text-slate-700">
          <div>
            <dt className="font-medium text-slate-900">En reparto</dt>
            <dd>El remito fue generado y el pedido está en camino.</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-900">Entregado, aguardando certificación</dt>
            <dd>Se registró la entrega, pero todavía no se cargó la firma del receptor.</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-900">Certificado</dt>
            <dd>El remito fue firmado; se muestra el nombre del receptor y la fecha de firma.</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-900">Facturado</dt>
            <dd>El remito ya fue incluido en una factura.</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-900">Anulado</dt>
            <dd>El remito fue anulado; se muestra el motivo.</dd>
          </div>
        </dl>

        <dl className="flex flex-col gap-2 text-sm text-slate-700">
          <div>
            <dt className="font-medium text-slate-900">En revisión</dt>
            <dd>Tu solicitud de cambio todavía no fue revisada.</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-900">Aprobada</dt>
            <dd>Operaciones aprobó el cambio solicitado.</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-900">Rechazada</dt>
            <dd>Operaciones rechazó el cambio; el pedido queda como estaba.</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-900">Aplicada</dt>
            <dd>El cambio aprobado ya se reflejó en el pedido.</dd>
          </div>
        </dl>
      </section>
    </div>
  );
}
